import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { AppIcon, ICON_SIZES } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { SIDEBAR_COLLAPSED_WIDTH } from '@backrs_ui/common-ui/src/molecules/BackrsSidebar/BackrsSidebar';

import { Box, SxProps, Theme, Typography } from '@mui/material';

import { getVisiblePages } from './pagination.util';

const BUTTON_STRINGS = appStrings.buttonText;

export type PageButtonProps = Readonly<{
  display:
    | {
        type: 'PAGE_NUMBER';
        pageNumber: number;
      }
    | {
        type: 'ELLIPSIS';
      };
  currentPage: number;
  onClick: (page: number) => void;
}>;

const PageButton = ({ display, currentPage, onClick }: PageButtonProps) => {
  if (display.type === 'ELLIPSIS') {
    return <Box sx={getPageButtonStyles(false, true)}>...</Box>;
  }

  return (
    <Box
      sx={getPageButtonStyles(display.pageNumber === currentPage, false)}
      onClick={() => onClick(display.pageNumber)}
    >
      <Typography
        variant="text-sm-medium"
        color={
          display.pageNumber === currentPage
            ? 'Colors/Text/text-secondary-hover'
            : 'Colors/Text/text-tertiary'
        }
      >
        {display.pageNumber}
      </Typography>
    </Box>
  );
};

type NavigationButtonType = 'previous' | 'next' | 'back' | 'finish';

interface NavigationButtonConfig {
  text: string;
  iconName?: 'ArrowLeft' | 'ArrowRight';
  iconPosition?: 'left' | 'right';
}

// four possible navigation buttons
const NAVIGATION_BUTTONS: Record<NavigationButtonType, NavigationButtonConfig> = {
  previous: {
    text: BUTTON_STRINGS.previous,
    iconName: 'ArrowLeft',
    iconPosition: 'left',
  },
  next: {
    text: BUTTON_STRINGS.next,
    iconName: 'ArrowRight',
    iconPosition: 'right',
  },
  back: {
    text: BUTTON_STRINGS.back,
    iconPosition: 'left',
  },
  finish: {
    text: BUTTON_STRINGS.finish,
    iconPosition: 'right',
  },
};

type NavigationButtonProps = Readonly<{
  buttonType: NavigationButtonType;
  onClick?: () => void;
  disabled: boolean;
  screenSize: 'large' | 'small';
}>;

const NavigationButton = ({ buttonType, onClick, disabled, screenSize }: NavigationButtonProps) => {
  const config = NAVIGATION_BUTTONS[buttonType];
  const textIconColor = disabled ? 'Colors/Text/text-disabled' : 'Colors/Text/text-tertiary';

  return (
    <Box
      sx={previousNextButtonStyles(disabled, screenSize)}
      onClick={disabled ? undefined : onClick}
    >
      {
        config.iconPosition === 'left' &&
          (config.iconName ? (
            <AppIcon iconName={config.iconName} diameter="20px" color={textIconColor} />
          ) : (
            <Box sx={{ width: '20px' }} />
          )) // empty box to take up space
      }

      <Typography variant="text-sm-medium" color={({ backrs }) => backrs.colors[textIconColor]}>
        {config.text}
      </Typography>

      {
        config.iconPosition === 'right' &&
          (config.iconName ? (
            <AppIcon iconName={config.iconName} diameter="20px" color={textIconColor} />
          ) : (
            <Box sx={{ width: '20px' }} />
          )) // empty box to take up space
      }
    </Box>
  );
};

export type PaginationProps = Readonly<{
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  handlePrevPage: () => void;
  handleNextPage: () => void;
  withPageNumbers?: boolean;
  finishButtonOnClick?: () => void;
  backButtonOnClick?: () => void;
  maxWidth?: number;
}>;

export const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  handlePrevPage,
  handleNextPage,
  withPageNumbers = true,
  finishButtonOnClick,
  backButtonOnClick,
  maxWidth,
}: PaginationProps) => {
  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPages;

  // determine configuration for the previous/back and next/finish buttons
  const prevButtonType: NavigationButtonType =
    isFirstPage && backButtonOnClick ? 'back' : 'previous';
  const nextButtonType: NavigationButtonType =
    isLastPage && finishButtonOnClick ? 'finish' : 'next';

  const prevButtonHandler = isFirstPage ? backButtonOnClick : handlePrevPage;
  const nextButtonHandler = isLastPage ? finishButtonOnClick : handleNextPage;

  const prevButtonDisabled = isFirstPage && !backButtonOnClick;
  const nextButtonDisabled = isLastPage && !finishButtonOnClick;

  return (
    <Box sx={paginationContainerStyles(maxWidth)}>
      {/* LARGE SCREEN PREVIOUS BUTTON RENDER */}
      <NavigationButton
        buttonType={prevButtonType}
        onClick={prevButtonHandler}
        disabled={prevButtonDisabled}
        screenSize="large"
      />

      <Box sx={pageButtonsContainerStyles}>
        {withPageNumbers &&
          getVisiblePages(currentPage, totalPages).map((page, index) => (
            <PageButton
              key={`page-${page}-${index}`}
              display={page}
              currentPage={currentPage}
              onClick={onPageChange}
            />
          ))}
      </Box>

      {/* LARGE SCREEN NEXT BUTTON RENDER */}
      <NavigationButton
        buttonType={nextButtonType}
        onClick={nextButtonHandler}
        disabled={nextButtonDisabled}
        screenSize="large"
      />

      {/* SMALL SCREEN RENDER PREVIOUS/NEXT BUTTONS */}
      <Box sx={smallScreenPaginationContainerStyles}>
        <NavigationButton
          buttonType={prevButtonType}
          onClick={prevButtonHandler}
          disabled={prevButtonDisabled}
          screenSize="small"
        />

        <NavigationButton
          buttonType={nextButtonType}
          onClick={nextButtonHandler}
          disabled={nextButtonDisabled}
          screenSize="small"
        />
      </Box>
    </Box>
  );
};

const paginationContainerStyles =
  (maxWidth: number | undefined): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    gap: backrs.spacing['spacing-md'],
    justifyContent: {
      xs: 'flex-start',
      sm: 'space-between',
    },
    alignItems: {
      xs: 'flex-start',
      sm: 'center',
    },
    width: {
      xs: `calc(100% - ${SIDEBAR_COLLAPSED_WIDTH}px)`,
      md: `calc(100% - ${SIDEBAR_COLLAPSED_WIDTH}px - 56px - 56px + 2px)`,
      lg: `calc(100% - ${SIDEBAR_COLLAPSED_WIDTH}px - 56px - 56px + 2px)`,
    },
    // target iPad Mini
    '@media screen and (max-width: 1024px) and (max-height: 768px)': {
      width: `calc(100% - ${SIDEBAR_COLLAPSED_WIDTH}px)`,
      left: '72px',
      px: '12px',
    },

    // target medium size tablets
    '@media screen and (max-width: 1205px) and (max-height: 768px) and (min-width: 1200px)': {
      marginLeft: `${SIDEBAR_COLLAPSED_WIDTH}px`,
      width: `calc(100% - ${SIDEBAR_COLLAPSED_WIDTH}px)`,
      paddingRight: backrs.spacing['spacing-lg'],
    },
    px: {
      xs: backrs.spacing['spacing-md'],
      sm: backrs.spacing['spacing-4xl'],
      md: backrs.spacing['spacing-none'],
    },
    pb: {
      xs: backrs.spacing['spacing-lg'],
      sm: backrs.spacing['spacing-xl'],
      md: backrs.spacing['spacing-4xl'],
      lg: backrs.spacing['spacing-5xl'],
    },
    pt: {
      xs: backrs.spacing['spacing-lg'],
      sm: backrs.spacing['spacing-xl'],
      md: backrs.spacing['spacing-2xl'],
      lg: backrs.spacing['spacing-3xl'],
    },
    '@media (max-height: 768px)': {
      pb: backrs.spacing['spacing-2xl'],
    },
    borderTop: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
    backgroundColor: backrs.colors['Beige/25'],
    boxSizing: 'border-box',
    zIndex: 2,
    position: 'fixed',
    bottom: 0,
    flexDirection: {
      xs: 'column',
      sm: 'row',
    },
    left: {
      xs: `${SIDEBAR_COLLAPSED_WIDTH}px`,
      md: `calc(${SIDEBAR_COLLAPSED_WIDTH}px + 56px - 1px)`,
      lg: 'unset',
    },
    maxWidth: maxWidth ? `${maxWidth - 64 - 64}px` : '100%',
    flexWrap: 'wrap',
  });

const pageButtonsContainerStyles: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
  alignSelf: 'center',
  flexWrap: 'wrap',
  minHeight: ICON_SIZES['32px'],
  height: ICON_SIZES['32px'],
});

const getPageButtonStyles =
  (isCurrentPage: boolean, isEllipsis: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    width: ICON_SIZES['32px'],
    height: ICON_SIZES['32px'],
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: backrs.radius['radius-full'],
    cursor: isEllipsis || isCurrentPage ? 'default' : 'pointer',
    backgroundColor: isCurrentPage
      ? backrs.colors['Beige/100']
      : backrs.colors['Colors/Base/Transparent'],
    userSelect: 'none',
  });

const previousNextButtonStyles =
  (isDisabled: boolean, screenSize: 'large' | 'small'): SxProps<Theme> =>
  ({ backrs }) => ({
    justifyContent: 'center',
    alignItems: 'center',
    cursor: isDisabled ? 'default' : 'pointer',
    gap: backrs.spacing['spacing-sm'],
    userSelect: 'none',
    display: {
      xs: screenSize === 'large' ? 'none' : 'flex',
      sm: screenSize === 'large' ? 'flex' : 'none',
    },
  });

const smallScreenPaginationContainerStyles: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  boxSizing: 'border-box',
  flexWrap: 'wrap',
};
